/**
 * NotificationPlanner – ren funktion från appdata till planerade notiser. Inga timers, ingen
 * plattforms-API. Transportadaptrar (Capacitor LocalNotifications, Web Push via server) tar planen
 * och schemalägger den.
 *
 * Grundregler:
 *  - Larmpunkts-ID är stabilt: (post, förekomst, larm). Titelbyte skapar ALDRIG en ny larmpunkt.
 *  - Importerade absoluta larm bevaras exakt (samma ögonblick).
 *  - Passerade larm schemaläggs inte retroaktivt; de redovisas som missade.
 *  - Klar-markerade poster får inga fler notiser – utom när en källändring väntar på granskning.
 *  - EMAIL/PROCEDURE/okända larm schemaläggs aldrig.
 *  - Kapacitet: närmast kommande först; täckningsgränsen redovisas.
 */
import type { AppState, ISODate, ISOInstant, OccurrenceKey, TagInfo, Timing } from '../model.ts';
import { sha256Text, stableInt31 } from '../hash.ts';
import { Temporal, addDays, daysBetween, formatDateShort, formatTime24, hhmmToMinutes, startDay, timingInstants } from '../time.ts';
import { expandOccurrences } from '../recurrence.ts';
import type { ItemRef } from '../views.ts';
import { tagSortWeight } from '../tags.ts';

export interface AlarmPoint {
  pointId: string;
  ref: ItemRef;
  occurrenceKey: OccurrenceKey;
  alarmKey: string;
  fireAt: ISOInstant;
  eventDate: ISODate;
  eventStart: ISOInstant | null;
  timed: boolean;
  title: string;
  location?: string;
  tag?: TagInfo;
  categoryId: string;
  sensitive: boolean;
  origin: 'import' | 'local';
  /** Ändras när innehållet som notisen visar ändras – kontrolleras före utskick. */
  revision: string;
}

export function pointIdFor(ref: ItemRef, occurrenceKey: string, alarmKey: string): string {
  const r = ref.kind === 'local' ? `l:${ref.id}` : `s:${ref.key}`;
  return 'ap_' + sha256Text(`${r}|${occurrenceKey}|${alarmKey}`).slice(0, 24);
}

function alarmFireAt(a: { kind: 'absolute'; at: string } | { kind: 'relative'; offsetMinutes: number; related: 'start' | 'end' }, t: Timing, tz: string): Temporal.Instant {
  if (a.kind === 'absolute') return Temporal.Instant.from(a.at);
  const { start, end } = timingInstants(t, tz);
  return (a.related === 'end' ? end : start).add({ minutes: a.offsetMinutes });
}

export interface CollectOptions {
  from: Temporal.Instant;
  to: Temporal.Instant;
}

/** Alla aktiva larmpunkter vars tidpunkt ligger i [from, to). */
export function collectAlarmPoints(state: AppState, opts: CollectOptions): AlarmPoint[] {
  const tz = state.settings.timeZone;
  const points: AlarmPoint[] = [];
  // Förekomster: larm kan ligga upp till ~400 dagar före händelsen (stora relativa offset).
  const fromDate = opts.from.toZonedDateTimeISO(tz).toPlainDate().toString();
  const toDate = opts.to.toZonedDateTimeISO(tz).toPlainDate().toString();
  const expandFrom = addDays(fromDate, -2);
  /** Förekomster vars larm kan hamna i fönstret: händelsen ligger högst "lead" dagar efter fönstret. */
  const leadDays = (alarms: Array<{ kind: string; offsetMinutes?: number }>) =>
    Math.min(400, Math.ceil(Math.max(0, ...alarms.map((a) => (a.kind === 'relative' ? -(a.offsetMinutes ?? 0) : 0))) / 1440) + 2);
  const hasAbsolute = (alarms: Array<{ kind: string }>) => alarms.some((a) => a.kind === 'absolute');
  const inWindow = (i: Temporal.Instant) => Temporal.Instant.compare(i, opts.from) >= 0 && Temporal.Instant.compare(i, opts.to) < 0;

  for (const ev of Object.values(state.events)) {
    if (ev.deleted) continue;
    const cal = state.calendars[ev.calendarId];
    if (!cal) continue;
    const displayAlarms = ev.alarms.filter((a) => a.action === 'DISPLAY' || a.action === 'AUDIO');
    if (!displayAlarms.length) continue;
    const sensitive = !!state.categories[ev.categoryId]?.sensitive;
    const expandTo = hasAbsolute(displayAlarms) ? addDays(toDate, 400) : addDays(toDate, leadDays(displayAlarms));
    for (const occ of expandOccurrences(ev, expandFrom, expandTo, tz)) {
      const done = ev.recurrence ? !!ev.doneOccurrences?.[occ.key] : !!ev.done;
      if (done) continue;
      const title = occ.override?.title ?? ev.title;
      const location = occ.override?.location ?? ev.location;
      for (const a of displayAlarms) {
        const fire = alarmFireAt(a, occ.timing, tz);
        if (!inWindow(fire)) continue;
        const alarmKey = a.kind === 'absolute' ? `abs:${a.at}` : `rel:${a.offsetMinutes}:${a.related}`;
        const ref: ItemRef = { kind: 'local', id: ev.id };
        const eventStart = occ.timing.kind === 'timed' ? timingInstants(occ.timing, tz).start.toString({ smallestUnit: 'second' }) : null;
        points.push({
          pointId: pointIdFor(ref, occ.key, alarmKey),
          ref,
          occurrenceKey: occ.key,
          alarmKey,
          fireAt: fire.toString({ smallestUnit: 'second' }),
          eventDate: startDay(occ.timing, tz),
          eventStart,
          timed: occ.timing.kind === 'timed',
          title,
          ...(location ? { location } : {}),
          categoryId: ev.categoryId,
          sensitive,
          origin: 'local',
          revision: sha256Text(`${title}|${location ?? ''}|${JSON.stringify(occ.timing)}|${fire.toString()}`).slice(0, 16),
        });
      }
    }
  }

  for (const s of Object.values(state.sourceItems)) {
    if (s.archived) continue;
    const ov = state.overlays[s.key];
    // Klar → inga fler notiser, UTOM när källan ändrats efter klar-markering (granskning väntar):
    // då gäller källans nya påminnelser tills användaren tagit ställning.
    const reviewPending = ov?.review?.reason === 'sourceChangedWhileDone';
    if (ov?.done && !reviewPending) continue;
    const displayAlarms = s.data.alarms.filter((a) => a.action === 'DISPLAY' || a.action === 'AUDIO');
    if (!displayAlarms.length) continue;
    // Absoluta larm utan fönsterträff kan hoppas över billigt.
    if (!s.data.recurrence && displayAlarms.every((a) => a.kind === 'absolute') && !displayAlarms.some((a) => inWindow(Temporal.Instant.from((a as { at: string }).at)))) continue;
    const expandTo = hasAbsolute(displayAlarms) ? addDays(toDate, 400) : addDays(toDate, leadDays(displayAlarms));
    for (const occ of expandOccurrences(s.data, expandFrom, expandTo, tz)) {
      for (const a of displayAlarms) {
        const fire = alarmFireAt(a, occ.timing, tz);
        if (!inWindow(fire)) continue;
        const alarmKey = a.kind === 'absolute' ? `abs:${a.at}` : `rel:${a.offsetMinutes}:${a.related}`;
        const ref: ItemRef = { kind: 'source', key: s.key };
        points.push({
          pointId: pointIdFor(ref, occ.key, alarmKey),
          ref,
          occurrenceKey: occ.key,
          alarmKey,
          fireAt: fire.toString({ smallestUnit: 'second' }),
          eventDate: startDay(occ.timing, tz),
          eventStart: occ.timing.kind === 'timed' ? timingInstants(occ.timing, tz).start.toString({ smallestUnit: 'second' }) : null,
          timed: occ.timing.kind === 'timed',
          title: s.data.summary,
          tag: s.data.tag,
          categoryId: ov?.categoryId ?? 'myndighet',
          sensitive: true,
          origin: 'import',
          revision: sha256Text(`${s.contentHash}|${fire.toString()}`).slice(0, 16),
        });
      }
    }
  }
  points.sort((a, b) => a.fireAt.localeCompare(b.fireAt) || a.pointId.localeCompare(b.pointId));
  return points;
}

export interface SnoozeRecord {
  pointId: string;
  /** Antal gånger punkten snoozats. */
  count: number;
  fireAt: ISOInstant;
  title: string;
  ref: ItemRef;
  sensitive: boolean;
}

export interface PlannedNotification {
  /** Stabilt sträng-ID (larmpunkt, sammanfattning eller snooze). */
  id: string;
  /** Numeriskt ID för plattformar som kräver det (Android/iOS via Capacitor). */
  numericId: number;
  fireAt: ISOInstant;
  title: string;
  body: string;
  /** Låsskärmsversion utan känsliga detaljer. */
  publicTitle: string;
  publicBody: string;
  sensitive: boolean;
  kind: 'single' | 'summary' | 'snooze';
  pointIds: string[];
  /** Posten som öppnas vid tryck. */
  open: ItemRef & { occurrenceKey?: string };
  /** Flyttad till slutet av tysta tider. */
  deferredByQuietHours: boolean;
  /** Levereras under tysta tider eftersom händelsen börjar innan de tar slut. */
  breaksQuietHours: boolean;
  /** Engelsk text (bara med PlanOptions.alsoEnglish). */
  en?: NotificationText;
}

export interface PlanOptions {
  now: Temporal.Instant;
  horizonDays: number;
  /** Max samtidigt schemalagda notiser på enheten (iOS: 64; Android-plugin: begränsa själv). */
  capacity: number;
  snoozes?: SnoozeRecord[];
  /** Punkter som redan levererats (från leveransloggen) – planeras inte igen. */
  delivered?: Set<string>;
  /** För sammanfattning av missade: senast appen planerade. */
  lastPlannedAt?: Temporal.Instant | null;
  /**
   * Språk för notistexten (enhetens val). Standard svenska – servern planerar på svenska tills enheten
   * själv översätter vid visning. Påverkar bara text, aldrig tider, ID:n eller vilka punkter som planeras.
   */
  lang?: NotifyLang;
  /**
   * Servern: lägg även till engelsk text (PlannedNotification.en). Enheten väljer vid visning efter sitt
   * språkval; den svenska texten, ID:n och revisionen är oförändrade.
   */
  alsoEnglish?: boolean;
}

export type NotifyLang = 'sv' | 'en';
export interface NotificationText {
  title: string;
  body: string;
  publicTitle: string;
  publicBody: string;
}

export interface NotificationPlan {
  generatedAt: ISOInstant;
  scheduled: PlannedNotification[];
  /** Larmpunkter inom horisonten som inte fick plats p.g.a. kapacitet. */
  overflowPoints: number;
  /** Lokala notiser schemalagda till och med denna tidpunkt (null = inga). */
  coverageUntil: ISOInstant | null;
  horizonEnd: ISOInstant;
  totalPointsInHorizon: number;
  /** Punkter som passerade sedan lastPlannedAt utan att vara levererade här – visas i sammanfattning. */
  missed: AlarmPoint[];
}

const PUBLIC_TITLE = 'AutoDay-påminnelse';
const PUBLIC_BODY = 'Öppna AutoDay för detaljer.';
const PUBLIC_EN = { title: 'AutoDay reminder', body: 'Open AutoDay for details.' };
const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dateShort = (d: ISODate, lang: NotifyLang) => (lang === 'en' ? `${Number(d.slice(8, 10))} ${EN_MONTHS[Number(d.slice(5, 7)) - 1]}` : formatDateShort(d));
const time24 = (i: Temporal.Instant, tz: string, lang: NotifyLang) => (lang === 'en' ? formatTime24(i, tz).replace('.', ':') : formatTime24(i, tz));
export function publicText(lang: NotifyLang = 'sv'): { title: string; body: string } {
  return lang === 'en' ? PUBLIC_EN : { title: PUBLIC_TITLE, body: PUBLIC_BODY };
}

function inQuiet(local: Temporal.ZonedDateTime, start: number, end: number): boolean {
  const m = local.hour * 60 + local.minute;
  return start <= end ? m >= start && m < end : m >= start || m < end;
}

function quietEnd(local: Temporal.ZonedDateTime, start: number, end: number): Temporal.ZonedDateTime {
  const m = local.hour * 60 + local.minute;
  let day = local.toPlainDate();
  if (start > end && m >= start) day = day.add({ days: 1 });
  return day.toZonedDateTime({ timeZone: local.timeZoneId, plainTime: Temporal.PlainTime.from({ hour: Math.floor(end / 60), minute: end % 60 }) });
}

function relLabel(eventDate: ISODate, fireDate: ISODate, lang: NotifyLang = 'sv'): string {
  const d = daysBetween(fireDate, eventDate);
  if (lang === 'en') return d === 0 ? 'today' : d === 1 ? 'tomorrow' : d > 1 ? `in ${d} days` : 'date passed';
  if (d === 0) return 'idag';
  if (d === 1) return 'imorgon';
  if (d > 1) return `om ${d} dagar`;
  return 'datum passerat';
}

/** Text för en larmpunkt. Exporteras så att enheten kan visa samma text på sitt eget språk. */
export function singleText(p: Pick<AlarmPoint, 'fireAt' | 'timed' | 'eventStart' | 'eventDate' | 'title' | 'location' | 'origin'>, tz: string, lang: NotifyLang = 'sv'): { title: string; body: string } {
  const fireDate = Temporal.Instant.from(p.fireAt).toZonedDateTimeISO(tz).toPlainDate().toString();
  if (p.timed && p.eventStart) {
    const t = time24(Temporal.Instant.from(p.eventStart), tz, lang);
    const d = daysBetween(fireDate, p.eventDate);
    const day = lang === 'en' ? (d === 0 ? 'Today' : d === 1 ? 'Tomorrow' : dateShort(p.eventDate, lang)) : d === 0 ? 'Idag' : d === 1 ? 'Imorgon' : formatDateShort(p.eventDate);
    return { title: p.title, body: `${day} ${lang === 'en' ? 'at' : 'kl.'} ${t}${p.location ? ' · ' + p.location : ''}` };
  }
  const prefix = p.origin === 'import' ? (lang === 'en' ? 'Imported date' : 'Importerat datum') : lang === 'en' ? 'All day' : 'Heldag';
  return { title: p.title, body: `${prefix} ${dateShort(p.eventDate, lang)} (${relLabel(p.eventDate, fireDate, lang)})` };
}

export function planNotifications(state: AppState, opts: PlanOptions): NotificationPlan {
  const tz = state.settings.timeZone;
  const horizonEnd = opts.now.add({ hours: 24 * opts.horizonDays });
  const delivered = opts.delivered ?? new Set<string>();
  const qh = state.settings.quietHours;
  const qs = qh.enabled ? hhmmToMinutes(qh.start) : 0;
  const qe = qh.enabled ? hhmmToMinutes(qh.end) : 0;
  // Tysta tider skjuter fram en påminnelse till tysta tidens slut. En sådan påminnelse väntar alltså
  // fortfarande fast dess ursprungliga larmtid har passerat: titta därför upp till ett dygn bakåt och
  // sortera bort först efter uppskjutningen (annars avbokades den vid varje senare omplanering).
  const from = qh.enabled ? opts.now.subtract({ hours: 24 }) : opts.now;
  const points = collectAlarmPoints(state, { from, to: horizonEnd }).filter((p) => !delivered.has(p.pointId));

  const out: PlannedNotification[] = [];
  const hide = state.settings.hideSensitiveOnLockScreen;
  const lang: NotifyLang = opts.lang === 'en' ? 'en' : 'sv';
  const pub = publicText(lang);

  // Sammanfattning: importerade HELDAGSposter med samma larmögonblick. Tidsbokade aktiviteter grupperas aldrig.
  const groups = new Map<string, AlarmPoint[]>();
  const singles: AlarmPoint[] = [];
  for (const p of points) {
    if (state.settings.summarizeImportedAllDay && p.origin === 'import' && !p.timed) {
      const k = p.fireAt;
      groups.set(k, [...(groups.get(k) ?? []), p]);
    } else singles.push(p);
  }
  for (const [, g] of groups) {
    if (g.length === 1) singles.push(g[0]!);
    else {
      const sorted = [...g].sort((a, b) => tagSortWeight(a.tag ?? { tag: null, rawPrefix: null }) - tagSortWeight(b.tag ?? { tag: null, rawPrefix: null }) || a.eventDate.localeCompare(b.eventDate));
      const top = sorted[0]!;
      const fireDate = Temporal.Instant.from(top.fireAt).toZonedDateTimeISO(tz).toPlainDate().toString();
      const id = 'grp_' + sha256Text(sorted.map((p) => p.pointId).sort().join(',')).slice(0, 20);
      out.push({
        id,
        numericId: stableInt31(id),
        fireAt: top.fireAt,
        title: lang === 'en' ? `${g.length} imported dates coming up` : `${g.length} importerade datum närmar sig`,
        body:
          lang === 'en'
            ? `First: ${top.title} – ${dateShort(top.eventDate, lang)} (${relLabel(top.eventDate, fireDate, lang)}). All are under Deadlines.`
            : `Först: ${top.title} – ${formatDateShort(top.eventDate)} (${relLabel(top.eventDate, fireDate)}). Alla finns under Viktigt.`,
        publicTitle: pub.title,
        publicBody: lang === 'en' ? `${g.length} reminders. ${pub.body}` : `${g.length} påminnelser. ${pub.body}`,
        ...(opts.alsoEnglish
          ? {
              en: {
                title: `${g.length} imported dates coming up`,
                body: `First: ${top.title} – ${dateShort(top.eventDate, 'en')} (${relLabel(top.eventDate, fireDate, 'en')}). All are under Deadlines.`,
                publicTitle: PUBLIC_EN.title,
                publicBody: `${g.length} reminders. ${PUBLIC_EN.body}`,
              },
            }
          : {}),
        sensitive: true,
        kind: 'summary',
        pointIds: sorted.map((p) => p.pointId),
        open: top.ref,
        deferredByQuietHours: false,
        breaksQuietHours: false,
      });
    }
  }
  for (const p of singles) {
    const txt = singleText(p, tz, lang);
    const en = opts.alsoEnglish ? singleText(p, tz, 'en') : null;
    out.push({
      id: p.pointId,
      numericId: stableInt31(p.pointId),
      fireAt: p.fireAt,
      title: txt.title,
      body: txt.body,
      publicTitle: pub.title,
      publicBody: pub.body,
      ...(en ? { en: { title: en.title, body: en.body, publicTitle: PUBLIC_EN.title, publicBody: PUBLIC_EN.body } } : {}),
      sensitive: p.sensitive,
      kind: 'single',
      pointIds: [p.pointId],
      open: { ...p.ref, occurrenceKey: p.occurrenceKey },
      deferredByQuietHours: false,
      breaksQuietHours: false,
    });
  }
  for (const s of opts.snoozes ?? []) {
    if (Temporal.Instant.compare(Temporal.Instant.from(s.fireAt), from) < 0) continue;
    const id = `${s.pointId}:s${s.count}`;
    out.push({
      id,
      numericId: stableInt31(id),
      fireAt: s.fireAt,
      title: s.title,
      body: lang === 'en' ? 'Snoozed reminder' : 'Snoozad påminnelse',
      publicTitle: pub.title,
      publicBody: pub.body,
      ...(opts.alsoEnglish ? { en: { title: s.title, body: 'Snoozed reminder', publicTitle: PUBLIC_EN.title, publicBody: PUBLIC_EN.body } } : {}),
      sensitive: s.sensitive,
      kind: 'snooze',
      pointIds: [s.pointId],
      open: s.ref,
      deferredByQuietHours: false,
      breaksQuietHours: false,
    });
  }

  // Tysta tider.
  if (qh.enabled) {
    for (const n of out) {
      const local = Temporal.Instant.from(n.fireAt).toZonedDateTimeISO(tz);
      if (!inQuiet(local, qs, qe)) continue;
      const endZ = quietEnd(local, qs, qe);
      const p = points.find((x) => x.pointId === n.pointIds[0]);
      const eventStart = p?.eventStart ? Temporal.Instant.from(p.eventStart) : null;
      if (eventStart && Temporal.Instant.compare(eventStart, endZ.toInstant()) <= 0) {
        n.breaksQuietHours = true; // T.ex. läkartid 07.30 – får inte gömmas till efter tysta tider.
      } else {
        n.fireAt = endZ.toInstant().toString({ smallestUnit: 'second' });
        n.deferredByQuietHours = true;
      }
    }
  }

  if (hide) {
    for (const n of out) {
      if (!n.sensitive) {
        n.publicTitle = n.title;
        n.publicBody = n.body;
      }
    }
  } else {
    for (const n of out) {
      n.publicTitle = n.title;
      n.publicBody = n.body;
    }
  }

  // Bara det som ännu inte passerat (efter ev. uppskjutning) planeras.
  const pending = out.filter((n) => Temporal.Instant.compare(Temporal.Instant.from(n.fireAt), opts.now) >= 0);
  const live = new Set(pending.filter((n) => n.kind !== 'snooze').flatMap((n) => n.pointIds));
  // Passerade punkter sedan förra planeringen – utom de som väntar på tysta tidens slut.
  const missed = opts.lastPlannedAt
    ? collectAlarmPoints(state, { from: opts.lastPlannedAt, to: opts.now }).filter((p) => !delivered.has(p.pointId) && !live.has(p.pointId))
    : [];

  pending.sort((a, b) => a.fireAt.localeCompare(b.fireAt) || a.id.localeCompare(b.id));
  const scheduled = pending.slice(0, Math.max(0, opts.capacity));
  const overflow = pending.length - scheduled.length;
  const coverageUntil = scheduled.length === 0 ? null : overflow > 0 ? scheduled.at(-1)!.fireAt : horizonEnd.toString({ smallestUnit: 'second' });
  return {
    generatedAt: opts.now.toString({ smallestUnit: 'second' }),
    scheduled,
    overflowPoints: overflow,
    coverageUntil,
    horizonEnd: horizonEnd.toString({ smallestUnit: 'second' }),
    totalPointsInHorizon: live.size,
    missed,
  };
}

export const SNOOZE_OPTIONS = [
  { id: 'snooze10', minutes: 10, label: 'Påminn om 10 minuter' },
  { id: 'snooze60', minutes: 60, label: 'Påminn om 1 timme' },
] as const;

/**
 * Snooze flyttar bara notisen – aldrig aktiviteten eller myndighetsdatumet. Begränsat antal gånger.
 */
export function snooze(
  existing: SnoozeRecord[],
  point: { pointId: string; title: string; ref: ItemRef; sensitive: boolean },
  minutes: number,
  now: Temporal.Instant,
  maxSnoozes: number,
): { snoozes: SnoozeRecord[]; ok: boolean; reason?: string } {
  const prev = existing.find((s) => s.pointId === point.pointId);
  const count = (prev?.count ?? 0) + 1;
  if (count > maxSnoozes) return { snoozes: existing, ok: false, reason: `Max ${maxSnoozes} snoozningar per påminnelse.` };
  const rec: SnoozeRecord = { ...point, count, fireAt: now.add({ minutes }).toString({ smallestUnit: 'second' }) };
  return { snoozes: [...existing.filter((s) => s.pointId !== point.pointId), rec], ok: true };
}

/** Kontroll strax före utskick: finns punkten kvar med samma revision och är posten inte klar? */
export function isPointStillValid(state: AppState, pointId: string, revision: string, fireAt: ISOInstant): boolean {
  const at = Temporal.Instant.from(fireAt);
  const pts = collectAlarmPoints(state, { from: at.subtract({ hours: 1 }), to: at.add({ hours: 1 }) });
  const p = pts.find((x) => x.pointId === pointId);
  return !!p && p.revision === revision;
}
